import React, { useState } from "react";
import PropTypes from "prop-types";
import styles from "./Footer.module.css";
import FooterLinks from "../FooterLinks";

const Footer = ({ className = "", showAccordions = true }) => {
  const [expandedFaqs, setExpandedFaqs] = useState([]);

  const faqs = [
    {
      id: 1,
      question: "Why choose ENGIE?",
      answer: [
        <>
          In a competitive business energy market, ENGIE is different.
          <br />
          <br />
          ENGIE has a long heritage, yet we're a new kind of energy company.
          We're leading the way in innovative services and sustainable energy
          solutions and are committed to helping UK businesses reduce their
          energy costs and carbon footprint.
          <br />
          <br />
        </>,
        "*Typically, you're a large energy user if: you have more than 20 sites; use more than 1GWh of electricity; or more than 0.75GWh of gas a year.",
      ],
    },
    {
      id: 2,
      question: "Who are the consultants and partners we work with?",
      answer: [
        <>
          For a complete list of the consultants and partners we work with,
          please{" "}
          <a
            href="https://www.engie.co.uk/wp-content/uploads/2024/12/6.4-Our-Partners-24.11.21.pdf"
            className={styles.termsLink}
            aria-label="Download ENGIE consultants and brokers list PDF"
            target="_blank"
            rel="noopener noreferrer"
          >
            click here
          </a>.
        </>,
      ],
    },
    {
      id: 3,
      question: "Does ENGIE supply large businesses?",
      answer: [
        <>
          Yes, if you're a large energy user*, or if you're a small business
          energy user with more than one business site, call us on{" "}
          <a
            href="tel:03336660276"
            className={styles.phoneLink}
            aria-label="Call ENGIE at 0333 666 0276"
          >
            0333 666 0276
          </a>{" "}
          to discuss your needs and get a quote.
          <br />
          <br />
        </>,
        "*Typically, you're a large energy user if: you have more than 20 sites; use more than 1GWh of electricity; or more than 0.75GWh of gas a year.",
      ],
    },
    {
      id: 4,
      question: "How long does it take to switch my energy account?",
      answer: [
        "If you're at the end of your contract and not in debt to your current energy supplier, it should take 21 days to transfer to ENGIE. If you wish, you can choose a later contract start date.",
      ],
    },
    {
      id: 5,
      question:
        "I'm an existing customer can I get a quote for a contract renewal online?",
      answer: [
        <>
          To ensure we give you the best renewal quote for your business needs,
          please call us on{" "}
          <a
            href="tel:03336660276"
            className={styles.phoneLink}
            aria-label="Call ENGIE at 0333 666 0276"
          >
            0333 666 0276
          </a>.
        </>,
      ],
    },
  ];

  const toggleFaq = (faqId) => {
    const isExpanded = expandedFaqs.includes(faqId);
    setExpandedFaqs(
      isExpanded
        ? expandedFaqs.filter((id) => id !== faqId)
        : [...expandedFaqs, faqId],
    );

    // Announce FAQ toggle to screen readers
    const faq = faqs.find((f) => f.id === faqId);
    if (faq) {
      const announcement = document.createElement("div");
      announcement.setAttribute("aria-live", "polite");
      announcement.textContent = isExpanded
        ? `FAQ "${faq.question}" collapsed`
        : `FAQ "${faq.question}" expanded`;
      announcement.style.position = "absolute";
      announcement.style.left = "-10000px";
      document.body.appendChild(announcement);
      setTimeout(() => announcement.remove(), 1000);
    }
  };

  const footerClasses = [styles.footer, className].filter(Boolean).join(" ");

  return (
    <footer
      className={footerClasses}
      role="contentinfo"
      aria-labelledby="footer-heading"
    >
      <div className={styles.content}>
        {/* Terms and conditions notice */}
        {showAccordions && (
          <div className={styles.termsNotice}>
            *{" "}
            <a
              href="https://www.engie.co.uk/supply-information/contractual-information/"
              target="_blank"
              className={styles.termsLink}
              aria-label="Read full terms and conditions (opens in same page)"
              rel="noopener noreferrer"
            >
              Terms and conditions
            </a> apply
          </div>
        )}

        {/* Help & FAQs Section */}
        {showAccordions && (
          <>
            <h3 id="footer-heading" className={styles.title}>
              Help & FAQs
            </h3>
            <section
              className={styles.faqList}
              aria-labelledby="footer-heading"
            >
              {faqs.map((faq) => (
                <article key={faq.id} className={styles.faqItem}>
                  <button
                    className={styles.faqQuestion}
                    onClick={() => toggleFaq(faq.id)}
                    aria-expanded={expandedFaqs.includes(faq.id)}
                    aria-controls={`faq-answer-${faq.id}`}
                    id={`faq-question-${faq.id}`}
                    type="button"
                  >
                    <span className={styles.questionText}>{faq.question}</span>
                    <svg
                      className={`${styles.arrow} ${expandedFaqs.includes(faq.id) ? styles.expanded : ""}`}
                      aria-hidden="true"
                      width="16"
                      height="16"
                      viewBox="0 0 17 17"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M16.354 5.075l-7.855 7.854-7.853-7.854 0.707-0.707 7.145 7.146 7.148-7.147 0.708 0.708z"
                        fill="currentColor"
                      />
                    </svg>
                  </button>

                  <div
                    id={`faq-answer-${faq.id}`}
                    className={`${styles.faqAnswer} ${expandedFaqs.includes(faq.id) ? styles.expanded : ""}`}
                    aria-labelledby={`faq-question-${faq.id}`}
                  >
                    {Array.isArray(faq.answer) ? (
                      faq.answer.map((paragraph, index) => (
                        <p key={`faq-${faq.id}-answer-${index}`}>{paragraph}</p>
                      ))
                    ) : (
                      <p>{faq.answer}</p>
                    )}
                  </div>
                </article>
              ))}
            </section>
            <br /> <br />
          </>
        )}

        {/* Contact section */}
        <section
          id="contact-section"
          className={styles.contactSection}
          aria-labelledby="contact-heading"
        >
          <h4 id="contact-heading" className={styles.contactTitle}>
            Prefer to call us?
          </h4>
          <div className={styles.contactInfo}>
            <p>
              Call us on{" "}
              <strong>
                <a
                  href="tel:03336660276"
                  className={styles.phoneLink}
                  aria-label="Call ENGIE at 0333 666 0276"
                >
                  0333 666 0276
                </a>
              </strong>{" "}
              to speak to a dedicated Business Energy Advisor
            </p>
            <p>
              <time>
                Lines open Mon – Thu 8.30am – 5.00pm and Fri 8.30am – 4.30pm.
              </time>
            </p>
          </div>
        </section>

        {/* Footer links */}
        <FooterLinks />
      </div>
    </footer>
  );
};

export default Footer;

Footer.propTypes = {
  className: PropTypes.string,
  showAccordions: PropTypes.bool,
};
